import { useMemo, useState } from "react";
import { Badge, Box, Card, Flex, Grid, Heading, SegmentedControl, Skeleton, Text } from "@radix-ui/themes";
import { Eye, MousePointerClick, Navigation, Phone, TrendingDown, TrendingUp } from "lucide-react";
import { format } from "date-fns";
import { Area, AreaChart, CartesianGrid, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const metrics = [
  { key: "profile_views", label: "Profile views", icon: Eye },
  { key: "calls", label: "Customer calls", icon: Phone },
  { key: "directions", label: "Direction requests", icon: Navigation },
  { key: "website_clicks", label: "Website clicks", icon: MousePointerClick },
];

const compact = value => new Intl.NumberFormat("en-GB", { notation: "compact", maximumFractionDigits: 1 }).format(value || 0);

function rollingAverage(rows, key) {
  return rows.map((row, index) => {
    const window = rows.slice(Math.max(0, index - 6), index + 1);
    return { ...row, [key]: Math.round(window.reduce((sum, item) => sum + item[key], 0) / window.length) };
  });
}

function ActivityMetric({ metric, current, previous, selected, onSelect }) {
  const Icon = metric.icon;
  const difference = current - previous;
  const percent = previous ? Math.round((difference / previous) * 100) : null;
  const improving = difference >= 0;
  return (
    <Card asChild size="2" variant={selected ? "surface" : "classic"}>
      <button type="button" onClick={onSelect} style={{ width: "100%", textAlign: "left", cursor: "pointer", borderColor: selected ? "var(--accent-8)" : undefined }}>
        <Flex direction="column" gap="2" minHeight="105px">
          <Flex align="center" gap="2"><Icon size={16} color="var(--accent-10)" /><Text size="2" weight="medium">{metric.label}</Text></Flex>
          <Flex align="baseline" gap="2"><Heading size="5">{compact(current)}</Heading>{difference !== 0 && <Text size="2" weight="bold" color={improving ? "green" : "red"}><Flex as="span" align="center" gap="1">{improving ? <TrendingUp size={13} /> : <TrendingDown size={13} />}{percent == null ? compact(Math.abs(difference)) : `${Math.abs(percent)}%`}</Flex></Text>}</Flex>
          <Text size="1" color="gray">vs previous equivalent period</Text>
        </Flex>
      </button>
    </Card>
  );
}

export default function CustomerActivity({ query }) {
  const [metricKey, setMetricKey] = useState("profile_views");
  const [mode, setMode] = useState("raw");
  const payload = query.data?.data;
  const selectedMetric = metrics.find(metric => metric.key === metricKey);
  const chartData = useMemo(() => {
    const current = mode === "trend" ? rollingAverage(payload?.series || [], metricKey) : payload?.series || [];
    const previous = mode === "trend" ? rollingAverage(payload?.previous_series || [], metricKey) : payload?.previous_series || [];
    return current.map((row, index) => ({ date: row.date, current: row[metricKey], previous: previous[index]?.[metricKey] || 0 }));
  }, [payload, metricKey, mode]);

  if (query.isLoading) return <Skeleton height="430px" />;
  if (query.isError) return <Card size="3"><Text color="red">Customer activity could not be loaded.</Text></Card>;
  if (!payload?.connected) return null;

  return (
    <Card size="3">
      <Flex direction="column" gap="4">
        <Flex justify="between" align="start" gap="4" wrap="wrap">
          <Flex direction="column" gap="1">
            <Heading as="h2" size="5">Customer activity</Heading>
            <Text size="2" color="gray">How customers found and contacted your Google Business Profile.</Text>
          </Flex>
          <Badge variant="soft" color="blue">Google Business Profile</Badge>
        </Flex>
        <Grid columns={{ initial: "1", sm: "2", lg: "4" }} gap="3">
          {metrics.map(metric => <ActivityMetric key={metric.key} metric={metric} current={payload.totals?.[metric.key] || 0} previous={payload.previous_totals?.[metric.key] || 0} selected={metricKey === metric.key} onSelect={() => setMetricKey(metric.key)} />)}
        </Grid>
        <Flex align="center" gap="3" wrap="wrap">
          <SegmentedControl.Root value={mode} onValueChange={setMode} size="1">
            <SegmentedControl.Item value="raw">Raw data</SegmentedControl.Item>
            <SegmentedControl.Item value="trend">7-day trend</SegmentedControl.Item>
          </SegmentedControl.Root>
        </Flex>
        <Box height="300px" role="img" aria-label={`${selectedMetric.label} over the selected period`}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ left: -20, right: 12, top: 10, bottom: 0 }}>
              <defs><linearGradient id="activity-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="var(--accent-9)" stopOpacity={0.22} /><stop offset="100%" stopColor="var(--accent-9)" stopOpacity={0.02} /></linearGradient></defs>
              <CartesianGrid vertical={false} stroke="var(--gray-a4)" strokeDasharray="3 4" />
              <XAxis dataKey="date" tickFormatter={day => format(new Date(`${day}T00:00:00`), "d MMM")} minTickGap={45} axisLine={false} tickLine={false} tick={{ fill: "var(--gray-10)", fontSize: 11 }} />
              <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fill: "var(--gray-10)", fontSize: 11 }} />
              <Tooltip labelFormatter={day => format(new Date(`${day}T00:00:00`), "d MMM yyyy")} formatter={(value, name) => [Number(value).toLocaleString("en-GB"), name === "current" ? selectedMetric.label : "Previous period"]} contentStyle={{ background: "var(--color-panel-solid)", borderColor: "var(--gray-a5)", borderRadius: 8, padding: "7px 9px", fontSize: 11 }} />
              <Area type={mode === "trend" ? "monotone" : "linear"} dataKey="current" stroke="var(--accent-9)" strokeWidth={2} fill="url(#activity-fill)" isAnimationActive={false} />
              <Line type={mode === "trend" ? "monotone" : "linear"} dataKey="previous" stroke="var(--orange-9)" strokeWidth={1.5} strokeDasharray="4 4" dot={false} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Flex>
    </Card>
  );
}
